import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: ${new Date().getFullYear()}

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital, incluyendo tu nombre y dirección de correo electrónico.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y confirmar tu compra.
• Enviarte el acceso al protocolo y materiales adquiridos.
• Comunicarnos contigo sobre tu pedido.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros. Esto no incluye terceros de confianza que nos ayudan a operar nuestro sitio web o a servirte, siempre que dichas partes acuerden mantener esta información confidencial.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies.

6. Tus Derechos
Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento contactándonos directamente.

7. Cambios en esta Política
Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página.
`;
const termsContent = `
Términos y Condiciones

Última actualización: ${new Date().getFullYear()}

1. Aceptación de los Términos
Al acceder y realizar una compra en este sitio, aceptas estos términos y condiciones en su totalidad.

2. Producto Digital
El "Sistema de Sincronización Biológica" es un producto digital educativo. Al completar tu compra, recibirás acceso inmediato a los materiales digitales descritos en la página de venta.

3. Uso Personal
El contenido adquirido es para tu uso personal únicamente. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.

4. Resultados
Los resultados pueden variar de persona a persona. El protocolo se basa en principios de higiene del sueño y no sustituye el consejo médico profesional. Consulta siempre con tu médico antes de realizar cambios significativos en tus hábitos de salud.

5. Garantía de Devolución
Ofrecemos una garantía de devolución de 7 noches. Si después de aplicar el protocolo durante 7 noches consecutivas no notas ninguna mejora, puedes solicitar un reembolso completo sin preguntas.

6. Limitación de Responsabilidad
Este producto es de carácter educativo e informativo. No somos responsables por el uso indebido del contenido ni por resultados individuales. No reemplaza la consulta médica profesional.

7. Propiedad Intelectual
Todo el contenido, incluyendo textos, gráficos y materiales del protocolo, está protegido por derechos de autor.

8. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación.
`;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  const title = activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  return <>
      <footer className="py-8 px-4 bg-nightBlue border-t border-white/5" data-id="element-35">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-36">
          <p className="font-lato text-sm text-warmWhite/40" data-id="element-37">
            © {new Date().getFullYear()} Sueño Profundo. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-38">
            <button onClick={() => setActiveModal('privacy')} className="font-lato text-sm text-warmWhite/50 hover:text-gold transition-colors duration-200" data-id="element-39">
              Política de Privacidad
            </button>
            <span className="text-warmWhite/20" data-id="element-40">|</span>
            <button onClick={() => setActiveModal('terms')} className="font-lato text-sm text-warmWhite/50 hover:text-gold transition-colors duration-200" data-id="element-41">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-42">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-43">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="bg-warmWhite text-nightBlue rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" data-id="element-44">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-nightBlue/10 flex-shrink-0" data-id="element-45">
                <h3 className="font-montserrat font-semibold text-xl" data-id="element-46">
                  {title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-nightBlue/10 transition-colors" data-id="element-47">
                  <XIcon className="w-5 h-5 text-nightBlue/60" data-id="element-48" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto flex-1" data-id="element-49">
                <div className="font-lato text-base text-nightBlue/80 leading-relaxed whitespace-pre-line" data-id="element-50">
                  {content}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-nightBlue/10 flex-shrink-0" data-id="element-51">
                <button onClick={() => setActiveModal(null)} className="w-full py-3 bg-nightBlue text-warmWhite font-montserrat font-semibold rounded-xl hover:bg-nightBlueLighter transition-colors" data-id="element-52">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}